// Web: full-body talking deity. Plays the transparent focus clips (no per-frame keying) and draws a
// small feathered MuseTalk mouth patch over the tracked face while she speaks. Clip choice follows
// the conversation: idle while she talks (face stays forward), listening while the user talks,
// a random idle/gesture/listen otherwise. Every clip starts and ends on the same base frame.
import React, { useEffect, useRef } from 'react';
import { Asset } from 'expo-asset';
import { FOCUS_CLIPS } from './focusClips';
import { feathered, loadImage, makeVisemeEngine, smoothstep, tapAnalyser } from './visemeEngine';
import type { FullBodyPuppetProps } from './FullBodyPuppet';

const SWAP_FADE_S = 0.15;
const MOUTH_FADE_MS = 160; // the patch eases in/out around her speech
const SCORE_LO = 0.6; // face-match score below which the patch is hidden (head turned/tilted away)
const SCORE_HI = 0.75;
const DPR = 2; // mouth canvas resolution multiplier
const uriOf = (m: number) => Asset.fromModule(m).uri;
// WebKit (Safari, every iOS browser) only keys HEVC alpha; everything else gets VP9 alpha.
const useMov = typeof navigator !== 'undefined' && (/^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent) || /iP(hone|ad|od)/.test(navigator.userAgent));

export function FullBodyPuppet({ deity, audioTrack, deitySpeaking, userSpeaking, width, height, left, top, onFirstFrame }: FullBodyPuppetProps) {
  const set = FOCUS_CLIPS[deity];
  const aRef = useRef<HTMLVideoElement | null>(null);
  const bRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dsRef = useRef(deitySpeaking);
  dsRef.current = deitySpeaking;
  const usRef = useRef(userSpeaking);
  usRef.current = userSpeaking;
  const firstRef = useRef(onFirstFrame);
  firstRef.current = onFirstFrame;

  const analyserRef = useRef<{ an: AnalyserNode; ctx: AudioContext } | null>(null);
  useEffect(() => {
    if (!audioTrack) return;
    const tap = tapAnalyser(audioTrack);
    analyserRef.current = tap;
    return () => {
      analyserRef.current = null;
      tap?.close();
    };
  }, [audioTrack]);

  useEffect(() => {
    const vids = [aRef.current, bRef.current];
    const c = canvasRef.current;
    if (!set || !vids[0] || !vids[1] || !c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const v0 = vids[0];
    const list = (useMov ? set.mov : set.webm).map(uriOf);
    const { roles, clips: tracks, fps } = set.track;
    const IDLE = Math.max(0, roles.indexOf('idle'));
    const LISTEN = roles.indexOf('listen');
    const GESTURE = roles.indexOf('gesture');
    const clipOf = [-1, -1];
    let front = 0;
    let swapping = false;
    let stopped = false;
    let raf = 0;
    let lastRandom = IDLE;

    // While silent: mostly idle, sometimes a gesture or a listening beat, never the same gesture twice.
    const randomClip = () => {
      const r = Math.random();
      let n = r < 0.5 ? IDLE : r < 0.75 ? GESTURE : LISTEN;
      if (n < 0 || (n === lastRandom && n !== IDLE)) n = IDLE;
      lastRandom = n;
      return n;
    };
    const forcedClip = () => (dsRef.current ? IDLE : usRef.current && LISTEN >= 0 ? LISTEN : -1);
    const load = (i: number, clip: number) => {
      if (clipOf[i] === clip) return;
      clipOf[i] = clip;
      vids[i]!.src = list[clip];
      vids[i]!.load();
    };

    const swapIfDue = () => {
      const f = vids[front]!;
      if (swapping || !f.duration || (f.currentTime < f.duration - SWAP_FADE_S - 0.05 && !f.ended)) return;
      const bi = 1 - front;
      const forced = forcedClip();
      if (forced >= 0) load(bi, forced); // conversation needs a specific clip: swap to it (front holds its last frame)
      const back = vids[bi]!;
      if (back.readyState < 2) return;
      swapping = true;
      // Incoming clip sits fully opaque under the outgoing one; only the top fades (no see-through dip).
      back.style.transition = 'none';
      back.style.zIndex = '1';
      back.style.opacity = '1';
      back.currentTime = 0;
      void back.play().catch(() => {});
      f.style.zIndex = '2';
      f.style.transition = `opacity ${SWAP_FADE_S}s linear`;
      f.style.opacity = '0';
      window.setTimeout(() => {
        f.pause();
        f.style.zIndex = '0';
        const old = front;
        front = bi;
        swapping = false;
        const nf = forcedClip();
        load(old, nf >= 0 ? nf : randomClip());
      }, SWAP_FADE_S * 1000 + 30);
    };

    // Mouth patches (feathered once).
    const mouths: Record<string, HTMLCanvasElement> = {};
    void Promise.all(
      Object.entries(set.mouths).map(async ([n, m]) => {
        try { mouths[n] = feathered(await loadImage(uriOf(m))); } catch { /* skip this shape */ }
      }),
    );
    const engine = makeVisemeEngine(() => analyserRef.current, () => dsRef.current);
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    let mouthA = 0;
    let lastNow = performance.now();
    let drewMouth = false;

    const draw = (now: number) => {
      if (stopped) return;
      raf = requestAnimationFrame(draw);
      const dt = now - lastNow;
      lastNow = now;
      swapIfDue();
      const st = engine.step(now);
      mouthA = Math.min(1, Math.max(0, mouthA + (dsRef.current ? 1 : -1) * (dt / MOUTH_FADE_MS)));
      const f = vids[front]!;
      const tr = tracks[clipOf[front]];
      if (mouthA <= 0 || !tr || f.readyState < 2) {
        if (drewMouth) { ctx.clearRect(0, 0, c.width, c.height); drewMouth = false; }
        return;
      }
      const p = tr[Math.min(tr.length - 1, Math.floor(f.currentTime * fps))];
      const alpha = mouthA * smoothstep(SCORE_LO, SCORE_HI, p[4]);
      ctx.clearRect(0, 0, c.width, c.height);
      drewMouth = true;
      if (alpha <= 0.01) return;
      const [x, y, w, h] = p;
      if (st.fadeT < 1 && mouths[st.prev]) {
        ctx.globalAlpha = alpha;
        ctx.drawImage(mouths[st.prev], x, y, w, h);
      }
      if (mouths[st.cur]) {
        ctx.globalAlpha = alpha * (st.fadeT < 1 && mouths[st.prev] ? st.fadeT : 1);
        ctx.drawImage(mouths[st.cur], x, y, w, h);
      }
      ctx.globalAlpha = 1;
    };

    const onFirst = () => firstRef.current?.();
    v0.addEventListener('loadeddata', onFirst, { once: true });
    load(0, IDLE);
    load(1, randomClip());
    vids[0]!.style.opacity = '1';
    vids[0]!.style.zIndex = '1';
    vids[1]!.style.opacity = '0';
    vids[1]!.style.zIndex = '0';
    const tryPlay = () => void vids[front]!.play().catch(() => {});
    tryPlay();
    window.addEventListener('pointerdown', tryPlay, { once: true });
    raf = requestAnimationFrame(draw);
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      v0.removeEventListener('loadeddata', onFirst);
      window.removeEventListener('pointerdown', tryPlay);
      vids.forEach((v) => {
        v!.pause();
        v!.removeAttribute('src');
        v!.load();
      });
    };
  }, [set]);

  if (!set) return null;
  const [tw, th] = set.track.size;
  const vstyle = { position: 'absolute', left: 0, top: 0, width, height, objectFit: 'contain', pointerEvents: 'none' } as const;
  return (
    <div style={{ position: 'absolute', left, top, width, height, pointerEvents: 'none' }}>
      <video ref={aRef} muted playsInline preload="auto" poster={uriOf(set.poster)} style={vstyle} />
      <video ref={bRef} muted playsInline preload="auto" style={{ ...vstyle, opacity: 0 }} />
      <canvas ref={canvasRef} width={tw * DPR} height={th * DPR} style={{ ...vstyle, zIndex: 3 }} />
    </div>
  );
}
